import { DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule, MatDateRangeInput } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { ChargersService } from '../../chargers/chargers.service';
import { Charger, SessionHistory } from '../../models';
import { HistoryService } from '../history.service';


@Component({
	selector: 'app-session-history',
	imports: [MatTableModule, DatePipe, DecimalPipe, MatButtonModule, MatIconModule, MatProgressSpinnerModule, MatButtonToggleModule, MatSelectModule, MatFormFieldModule, MatDatepickerModule, MatDateRangeInput, MatPaginatorModule],
	templateUrl: './session-history.component.html',
	styleUrl: './session-history.component.scss',
	providers: [provideNativeDateAdapter()],
})
export class SessionHistoryComponent {
	displayedColumns: string[] = ['begin', 'end', 'chargerSerialNumber', 'totalChargingEnergy'];
	chargeSessionHistory = signal<SessionHistory[]>([]);
	chargeSessionHistoryLoaded = signal<boolean>(false);
	period = signal<'lday' | 'lweek' | 'lmonth'>('lday');
	chargers = signal<Charger[]>([]);
	chargersLoaded = signal<boolean>(false);
	chargersSelected = signal<Charger[]>([]);
	dateBegin = signal<Date | null>(null);
	dateEnd = signal<Date | null>(null);
	totalEnergy = computed(() =>
		this.chargeSessionHistory().reduce((sum, s) => sum + s.totalChargingEnergy, 0)
	);

	dataSource = new MatTableDataSource<SessionHistory>();

	@ViewChild(MatPaginator)
	set matPaginator(p: MatPaginator | undefined) {
		// this runs whenever the paginator appears/disappears in the view
		if (!p) return;

		this.paginator = p;
		this.dataSource.paginator = p;

		console.log('set matPaginator() connected, pageSize:', p.pageSize);
	}

	private paginator?: MatPaginator;

	private historyService = inject(HistoryService);
	private chargersService = inject(ChargersService);


	ngOnInit(): void {
		this.chargersService.getChargers().subscribe((chargers) => {
			console.log('getChargers():', chargers);
			this.chargers.set(chargers);
			if (chargers.length) {
				this.chargersSelected.set(chargers);
			}
			this.chargersLoaded.set(true);
		});

		this.setDateRange('lday');
	}


	constructor() {
		effect(() => {
			this.chargeSessionHistoryLoaded.set(false);

			console.log('effect — period:', this.period(), 'dateBegin:', this.dateBegin(), 'dateEnd:', this.dateEnd(), 'chargersSelected:', this.chargersSelected());

			const dateStart = this.dateBegin();
			const dateEnd = this.dateEnd();
			const chargers = this.chargersSelected();
			if ((dateStart) && (dateEnd) && (chargers.length)) {
				let timeBegin = Math.floor(dateStart.getTime() / 1000);
				let timeEnd = Math.floor(dateEnd.getTime() / 1000);
				this.historyService
					.getSessionHistory(timeBegin, timeEnd, chargers)
					.subscribe(history => {
						console.log('getSessionHistory():', history);
						this.chargeSessionHistory.set(history);
						this.dataSource.data = history;
						this.paginator?.firstPage(); // reset to first page
						this.chargeSessionHistoryLoaded.set(true);
					});
			} else {
				console.log('effect: no data — dateStart, dateEnd, or chargers not set');
				this.chargeSessionHistory.set([]);
				this.dataSource.data = [];
				this.paginator?.firstPage(); // reset to first page
				this.chargeSessionHistoryLoaded.set(true);
			}
		});
	}


	setDateRange(period: string) {
		const dateNow = new Date();
		const dateToday = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate());
		switch (period) {
			case 'lmonth':
				const dateFirstLastMonth = new Date(dateNow.getFullYear(), dateNow.getMonth() - 1);
				const dateFirstThisMonth = new Date(dateNow.getFullYear(), dateNow.getMonth());
				this.dateBegin.set(dateFirstLastMonth);
				this.dateEnd.set(dateFirstThisMonth);
				console.log('setDateRange(lmonth) — dateFirstLastMonth:', dateFirstLastMonth, 'dateFirstThisMonth:', dateFirstThisMonth);
				break;
			case 'lweek':
				const dayOfWeekToday = dateToday.getDay() || 7; // adjust when day is Sunday (0)
				const dateMondayLastWeek = new Date(dateToday);
				const dateMondayThisWeek = new Date(dateToday);
				dateMondayLastWeek.setDate(dateToday.getDate() - dayOfWeekToday - 6);
				dateMondayThisWeek.setDate(dateToday.getDate() - dayOfWeekToday + 1);
				this.dateBegin.set(dateMondayLastWeek);
				this.dateEnd.set(dateMondayThisWeek);
				console.log('setDateRange(lweek) — dateToday:', dateToday, 'dayOfWeekToday:', dayOfWeekToday, 'dateMondayLastWeek:', dateMondayLastWeek, 'dateMondayThisWeek:', dateMondayThisWeek);
				break;
			case 'lday':
			default:
				const dateYesterday = new Date(dateNow.getFullYear(), dateNow.getMonth(), dateNow.getDate() - 1);
				this.dateBegin.set(dateYesterday);
				this.dateEnd.set(dateToday);
				console.log('setDateRange(lday) — dateYesterday:', dateYesterday, 'dateToday:', dateToday);
				break;
		}
	}


	downloadCsv(): void {
		const fileName = `ratiosessionhistory-${this.formatDateYYYYMMDD(this.dateBegin())}-${this.formatDateYYYYMMDD(this.dateEnd())}-${this.formatChargers(this.chargersSelected())}.csv`;
		console.log('downloadCsv():', fileName);
		const content = this.getReadableData(this.chargeSessionHistory());
		if (content) {
			const csvContent = this.convertToCsv(content);
			const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = fileName;
			a.click();

			URL.revokeObjectURL(url);
		}
	}

	private convertToCsv(objArray: any[]): string {
		if (!objArray || objArray.length === 0) return '';

		const keys = Object.keys(objArray[0]);
		const header = keys.join(',');

		const rows = objArray.map(obj => 
			keys.map(k => `"${String(obj[k]).replace(/"/g, '""')}"`).join(',')
		);

		return [header, ...rows].join('\r\n');
	}

	private getReadableData(sessionHistory: SessionHistory[]) {
		return sessionHistory.map(item => ({
			begin: this.formatUnixTime(item.begin.time),
			end: this.formatUnixTime(item.end.time),
			chargerSerialNumber: item.chargerSerialNumber,
			totalChargingEnergy: item.totalChargingEnergy
		}));
	}

	private formatUnixTime(unixTime: number): string {
		if (!unixTime) return '';

		const date = new Date(Number(unixTime) * 1000);

		return new Intl.DateTimeFormat('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric'
		}).format(date);
	}

	private formatDateYYYYMMDD(date: Date | null): string {
		if (date) {
			date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
			return date.toISOString().slice(0,10).replace(/-/g,'');
		} else {
			return '';
		}
	}

	private formatChargers(chargers: Charger[]): string {
		return chargers.map(charger => charger.serialNumber).join('-')
	}
}
